import type { PolicyLink } from '../services/types'

/** Coarse insured asset class — use for listed-driver and update-policy routing. */
export type InsuredAssetClass = '4x4' | 'npv' | 'other'

/** Claim flow granularity (caravan vs camper vs 4x4). */
export type ClaimablePolicyType = '4x4' | 'caravan' | 'camper' | 'unsupported'

/**
 * Canonical NPV vs 4x4 check. Prefer API {@link PolicyLink.insuredAssetClass}; falls back to label/productRef
 * for cached policies loaded before the field existed.
 */
export function getInsuredAssetClass(policy: PolicyLink | null | undefined): InsuredAssetClass {
  if (!policy) return 'other'
  const fromApi = policy.insuredAssetClass
  if (fromApi === '4x4' || fromApi === 'npv' || fromApi === 'other') {
    return fromApi
  }
  return resolveInsuredAssetClassFromPolicyLink(policy)
}

/** @alias {@link getInsuredAssetClass} — `true` when insured asset is caravan or camper trailer. */
export function policyIsNonPoweredVehicle(policy: PolicyLink | null | undefined): boolean {
  return getInsuredAssetClass(policy) === 'npv'
}

/** `true` when insured asset is an active Club 4x4 motor policy. */
export function policyIs4x4Vehicle(policy: PolicyLink | null | undefined): boolean {
  return getInsuredAssetClass(policy) === '4x4'
}

/**
 * Whether the policy may use the in-portal Update policy flow (policy view → update-all).
 */
export function policySupportsOnlineDetailUpdate(policy: PolicyLink | null | undefined): boolean {
  const cls = getInsuredAssetClass(policy)
  return cls === '4x4' || cls === 'npv'
}

/**
 * Listed-driver add/edit in the portal.
 * Disabled while Entsia cannot apply listed-driver changes (CSP-38); users are directed to call centre.
 */
export function policySupportsListedDriverChanges(_policy: PolicyLink | null | undefined): boolean {
  return false
}

const UNSUPPORTED_VEHICLE_LABELS = new Set([
  'fifth wheeler',
  '5th wheeler',
  '5th wheel',
  'fifth wheel',
  'slide on',
  'slide-on',
  'slide on camper',
  'slide-on camper',
  // Legacy backend typo — older sessions may still carry this label.
  'side on',
])

/**
 * Returns true for fifth wheeler and slide-on camper product types.
 * These policies can view details and update payment, but cannot make claims
 * or update policy details online.
 */
export function policyIsUnsupportedVehicleType(policy: PolicyLink | null | undefined): boolean {
  if (!policy) return false
  const label = (policy.non4x4InsuredAssetLabel ?? '').trim().toLowerCase()
  if (UNSUPPORTED_VEHICLE_LABELS.has(label)) return true
  const ref = (policy.productRef ?? '').toUpperCase()
  return ref.includes('FIFTH') || ref.includes('5TH') || ref.includes('SLIDE')
}

function insuredAssetLabelIsCaravan(policy: PolicyLink): boolean {
  return (policy.non4x4InsuredAssetLabel ?? '').trim().toLowerCase() === 'caravan'
}

function insuredAssetLabelIsCamperTrailer(policy: PolicyLink): boolean {
  return (policy.non4x4InsuredAssetLabel ?? '').trim().toLowerCase() === 'camper trailer'
}

/**
 * Fallback when {@link PolicyLink.insuredAssetClass} is absent (e.g. session cache).
 * Order: NPV label → NPV productRef → explicit 4x4 flag → 4x4 productRef.
 */
export function resolveInsuredAssetClassFromPolicyLink(policy: PolicyLink): InsuredAssetClass {
  const label = (policy.non4x4InsuredAssetLabel ?? '').trim().toLowerCase()
  if (label === 'caravan' || label === 'camper trailer') return 'npv'
  if (label === 'daily drive') return 'other'

  const ref = (policy.productRef ?? '').toUpperCase()
  if (ref.includes('CARAVAN') || ref.includes('CAMPER')) return 'npv'

  if (policy.has4X4Vehicle === true) return '4x4'
  if (policy.has4X4Vehicle === false) return 'other'

  if (ref.includes('4X4')) return '4x4'
  return 'other'
}

/** Claim / pre-claim granularity within {@link InsuredAssetClass}. */
export function getPolicyType(policy: PolicyLink | null | undefined): ClaimablePolicyType {
  if (!policy) return 'unsupported'
  const cls = getInsuredAssetClass(policy)
  if (cls === '4x4') return '4x4'
  if (cls === 'npv') {
    const label = (policy.non4x4InsuredAssetLabel ?? '').trim().toLowerCase()
    if (label === 'camper trailer' || insuredAssetLabelIsCamperTrailer(policy)) return 'camper'
    if (label === 'caravan' || insuredAssetLabelIsCaravan(policy)) return 'caravan'
    const ref = (policy.productRef ?? '').toUpperCase()
    if (ref.includes('CAMPER')) return 'camper'
    if (ref.includes('CARAVAN')) return 'caravan'
    return 'unsupported'
  }
  return 'unsupported'
}
